import type { aiApi } from '@/features/ai/aiApi'
import type { instructionsApi } from '@/features/instructions/instructionsApi'
import type { articleModerationApi } from '../articleModerationApi'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export const REVIEW_IN_PROGRESS = 'review_in_progress'

// Имена проверяются компилятором против настоящих эндпоинтов: переименованная ручка
// ломает сборку, а не тихо выпадает из списка (и статья перестаёт замерзать по 409).
type ArticleEndpoint =
  | keyof typeof instructionsApi.endpoints
  | keyof typeof articleModerationApi.endpoints
  | keyof typeof aiApi.endpoints

/** Русская строка отказа — для старых потребителей-констант. Показывая тост, бери
 *  `articleSaveRefusedText()`: он на языке интерфейса. */
export const ARTICLE_SAVE_REFUSED = messages.ru.articleSaveRefused

/** Отказ сохранения статьи на проверке — на языке интерфейса. */
export const articleSaveRefusedText = (): string => messagesNow(messages).articleSaveRefused

type Messages = (typeof messages)['ru']

/**
 * Записи статьи, которые бэк отбивает `409 review_in_progress`, и текст тоста на этот
 * отказ. Единственный источник для `errorToastMiddleware`: принадлежность ручки к статье
 * решает и текст, и то, какой тег перечитать (`ArticleModeration`, а не `Moderation` курса).
 * Молчащие ручки (`SILENT_ENDPOINTS`) здесь ради тега — тост им не покажут.
 */
const saveRefused = (m: Messages): string => m.articleSaveRefused

const ARTICLE_TEXT: ReadonlyMap<string, (m: Messages) => string> = new Map<
  ArticleEndpoint,
  (m: Messages) => string
>([
  ['updateInstruction', saveRefused],
  ['setArticleVisibility', saveRefused],
  ['submitArticleUpdate', saveRefused],
  ['discardArticleDraft', saveRefused],
  ['translateArticle', (m) => m.articleTranslateRefused],
])

const articleText = (endpointName?: string): string | undefined =>
  endpointName ? ARTICLE_TEXT.get(endpointName)?.(messagesNow(messages)) : undefined

/** Тост на запись во время проверки: ручка статьи — своим текстом, всё прочее — курс. */
export const reviewInProgressText = (endpointName?: string): string =>
  articleText(endpointName) ?? messagesNow(messages).courseSaveRefused

/** Тег состояния проверки, устаревшего после отказа. Id сущности middleware неизвестен —
 *  сбрасывается тег целиком; у статьи он свой, её id живёт в другом пространстве, чем курса. */
export const reviewStateTag = (endpointName?: string): 'ArticleModeration' | 'Moderation' =>
  articleText(endpointName) === undefined ? 'Moderation' : 'ArticleModeration'
